import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon, InformationCircleIcon,
  MagnifyingGlassIcon, CpuChipIcon, ServerStackIcon,
  ChartBarIcon, CheckCircleIcon, ArrowPathIcon,
  AdjustmentsHorizontalIcon, XCircleIcon,
} from '@heroicons/react/24/outline';

import apiClient from '../../lib/apiClient';

interface ProviderHealth {
  uptime: number | null;
  avg_latency_ms: number | null;
  total: number;
  samples: boolean[];
}

interface HealthData {
  queue: { pending: number; failed: number };
  api: { total: number; success: number; error: number; errors_4xx: number; errors_5xx: number; success_rate: number };
  providers: { serper: ProviderHealth; openrouter: ProviderHealth };
  database: { total_users: number; total_searches: number; active_searches: number };
  new_users_today: number;
  searches_today: number;
  recent_searches: any[];
  failed_jobs: any[];
}

// "99.98% Uptime" style label from real 24h data (null = nothing recorded yet).
function fmtUptime(p?: ProviderHealth): string {
  return p && p.uptime != null ? `${p.uptime}% Uptime` : 'No data';
}

// Average latency of successful calls, formatted ms / s.
function fmtLatency(p?: ProviderHealth): string {
  if (!p || p.avg_latency_ms == null) return '—';
  return p.avg_latency_ms >= 1000 ? `${(p.avg_latency_ms / 1000).toFixed(1)}s` : `${p.avg_latency_ms}ms`;
}

// 20-segment health strip built from the last 20 real call outcomes. Missing
// samples pad on the left as neutral so the strip is always full-width.
function StatusBars({ samples, upColor }: { samples?: boolean[]; upColor: string }) {
  const recent = (samples ?? []).slice(-20);
  const padded = [...Array(Math.max(0, 20 - recent.length)).fill(null), ...recent];
  return (
    <div className="flex gap-1 h-2.5">
      {padded.map((s, i) => (
        <div
          key={i}
          className={`flex-1 ${s === null ? 'bg-slate-200' : s ? upColor : 'bg-red-400'} ${i === 0 ? 'rounded-l-full' : ''} ${i === 19 ? 'rounded-r-full' : ''}`}
        />
      ))}
    </div>
  );
}

function statusColor(status: string) {
  if (status === 'completed') return 'text-emerald-600';
  if (status === 'failed')    return 'text-red-600';
  return 'text-brand-600';
}

export default function SystemHealth() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);

  const fetch = useCallback((isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    apiClient.get('/admin/system-health')
      .then(({ data: d }) => { setData(d); setLastRefresh(new Date()); })
      .catch(() => {})
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const t = setInterval(() => fetch(true), 30000);
    return () => clearInterval(t);
  }, [fetch]);

  const navigate = useNavigate();
  const errorRate = data ? (data.api.total > 0 ? ((data.api.error / data.api.total) * 100).toFixed(2) : '0.00') : '0.00';
  const isHealthy = data ? (data.queue.failed === 0 && data.api.success_rate >= 90) : true;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto flex flex-col gap-6 pb-20">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 tracking-tight">System Health Telemetry</h1>
          <p className="text-[12px] text-slate-400 mt-1 font-mono">
            Last updated: {lastRefresh.toLocaleTimeString()} UTC
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => fetch(true)} disabled={refreshing}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-xl text-[13px] font-bold hover:bg-slate-50 disabled:opacity-50 transition-colors shadow-sm">
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border text-[11px] font-extrabold uppercase tracking-widest ${isHealthy ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-amber-50 border-amber-100 text-amber-700'}`}>
            <div className={`w-2 h-2 rounded-full animate-pulse ${isHealthy ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {isHealthy ? 'Live Update' : 'Issues Detected'}
          </div>
        </div>
      </div>

      {/* Alert Banners */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data && data.queue.failed > 0 ? (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-5 flex gap-4">
            <div className="w-10 h-10 bg-red-100 text-red-500 rounded-xl flex items-center justify-center shrink-0">
              <ExclamationTriangleIcon className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-[14px] font-extrabold text-red-800 mb-1">{data.queue.failed} Failed Queue Jobs</h4>
              <p className="text-[12px] text-red-700/80 font-medium leading-relaxed">
                Check the failed jobs table and retry or clear stale entries.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 flex gap-4">
            <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center shrink-0">
              <CheckCircleIcon className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-[14px] font-extrabold text-emerald-800 mb-1">Queue Healthy</h4>
              <p className="text-[12px] text-emerald-700 font-medium">
                {loading ? '—' : `${data?.queue.pending ?? 0} pending jobs. No failures.`}
              </p>
            </div>
          </div>
        )}

        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex gap-4">
          <div className="w-10 h-10 bg-blue-50 text-brand-500 rounded-xl flex items-center justify-center shrink-0">
            <InformationCircleIcon className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-[14px] font-extrabold text-slate-800 mb-1">Database — Normal Operation</h4>
            <p className="text-[12px] text-slate-500 font-medium">
              {loading ? '—' : `${data?.database.total_users.toLocaleString()} users · ${data?.database.total_searches.toLocaleString()} searches · ${data?.database.active_searches} active`}
            </p>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

        {/* Left Column */}
        <div className="xl:col-span-8 flex flex-col gap-6">

          {/* API Connectivity */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-[15px] font-extrabold text-slate-900">API Connectivity Health</h3>
              <span className="text-[12px] text-slate-400 font-medium">Last 24 Hours</span>
            </div>
            <div className="flex flex-col gap-6">
              {/* Serper */}
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-blue-50 text-brand-500 rounded-xl flex items-center justify-center shrink-0">
                  <MagnifyingGlassIcon className="w-6 h-6" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-[14px] font-bold text-slate-900">Serper API (Search)</h4>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-brand-50 text-brand-600 text-[10px] font-extrabold tracking-widest uppercase rounded">{fmtUptime(data?.providers?.serper)}</span>
                      <div className="text-right">
                        <p className="text-[14px] font-extrabold text-slate-900">{fmtLatency(data?.providers?.serper)}</p>
                        <p className="text-[9px] text-slate-400">Avg Latency</p>
                      </div>
                    </div>
                  </div>
                  <StatusBars samples={data?.providers?.serper?.samples} upColor="bg-brand-500" />
                </div>
              </div>
              {/* LLM */}
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center shrink-0">
                  <CpuChipIcon className="w-6 h-6" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-[14px] font-bold text-slate-900">LLM Core Engine</h4>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-[10px] font-extrabold tracking-widest uppercase rounded">{fmtUptime(data?.providers?.openrouter)}</span>
                      <div className="text-right">
                        <p className="text-[14px] font-extrabold text-slate-900">{fmtLatency(data?.providers?.openrouter)}</p>
                        <p className="text-[9px] text-slate-400">Avg Latency</p>
                      </div>
                    </div>
                  </div>
                  <StatusBars samples={data?.providers?.openrouter?.samples} upColor="bg-slate-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Load & Latency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Queue Jobs</p>
              <div className="flex items-baseline gap-3 mb-4">
                {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
                  <p className="text-3xl sm:text-[40px] font-extrabold text-slate-900 leading-none">{data?.queue.pending ?? 0}</p>}
                <span className="text-[12px] font-bold text-slate-500">pending</span>
              </div>
              <div className="flex items-center gap-3 text-[12px]">
                <span className={`font-extrabold ${(data?.queue.failed ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {data?.queue.failed ?? 0} failed
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">jobs in queue</span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">API Health (24h)</p>
              <div className="flex items-baseline gap-3 mb-4">
                {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
                  <p className={`text-3xl sm:text-[40px] font-extrabold leading-none ${(data?.api.success_rate ?? 100) >= 90 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {data?.api.success_rate ?? 100}%
                  </p>}
                <span className="text-[12px] font-bold text-slate-500">success rate</span>
              </div>
              <div className="flex items-center gap-3 text-[12px]">
                <span className="font-extrabold text-slate-700">{data?.api.total ?? 0}</span>
                <span className="text-slate-400">total requests</span>
              </div>
            </div>
          </div>

          {/* Error Distribution */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-[15px] font-extrabold text-slate-900">Error Distribution</h3>
                <p className="text-[12px] text-slate-400">Aggregated request failures — last 24h</p>
              </div>
            </div>

            <div className="border border-dashed border-slate-200 bg-slate-50/50 rounded-2xl h-[100px] flex flex-col items-center justify-center mb-6">
              <ChartBarIcon className="w-8 h-8 text-slate-200 mb-2" />
              <p className="text-[12px] font-mono text-slate-400">
                {loading ? 'Loading...' : `${errorRate}% global error rate — ${Number(errorRate) < 1 ? 'Healthy' : 'Monitor'}`}
              </p>
            </div>

            <div className="flex items-center justify-between px-4">
              <div className="text-center">
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">4XX Client</p>
                <p className="text-[22px] font-extrabold text-slate-900">{loading ? '—' : data?.api.errors_4xx ?? 0}</p>
              </div>
              <div className="w-px h-10 bg-slate-200" />
              <div className="text-center">
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">5XX Server</p>
                <p className={`text-[22px] font-extrabold ${(data?.api.errors_5xx ?? 0) > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {loading ? '—' : data?.api.errors_5xx ?? 0}
                </p>
              </div>
              <div className="w-px h-10 bg-slate-200" />
              <div className="text-center">
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">Queue Failed</p>
                <p className={`text-[22px] font-extrabold ${(data?.queue.failed ?? 0) > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {loading ? '—' : data?.queue.failed ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: System Events */}
        <div className="xl:col-span-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2.5">
              <ServerStackIcon className="w-5 h-5 text-slate-500" />
              <h3 className="text-[15px] font-extrabold text-slate-900">System Events</h3>
            </div>
            <button className="text-slate-400 hover:text-slate-700"><AdjustmentsHorizontalIcon className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 relative overflow-y-auto max-h-[480px]">
            <div className="absolute left-[15px] top-4 bottom-4 w-px bg-slate-200 z-0" />
            <div className="flex flex-col gap-6 relative z-10">

              {/* Today's metrics */}
              {!loading && data && (
                <>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm">
                      <ChartBarIcon className="w-4 h-4 text-brand-500" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-slate-900 mb-0.5">Today's Activity</p>
                      <p className="text-[12px] text-slate-500 leading-snug">
                        {data.new_users_today} new users · {data.searches_today} searches today
                      </p>
                      <span className="text-[10px] font-mono text-slate-400">Live</span>
                    </div>
                  </div>
                </>
              )}

              {/* Recent searches */}
              {loading ? (
                Array.from({length:4}).map((_,i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-slate-100 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-4 bg-slate-100 animate-pulse rounded w-3/4" />
                      <div className="h-3 bg-slate-50 animate-pulse rounded w-full" />
                    </div>
                  </div>
                ))
              ) : (data?.recent_searches ?? []).map((s: any) => (
                <div key={s.id} className="flex items-start gap-4">
                  <div className={`w-8 h-8 rounded-full bg-white border flex items-center justify-center shrink-0 shadow-sm ${s.status==='failed'?'border-red-200':'border-slate-200'}`}>
                    {s.status === 'failed'
                      ? <XCircleIcon className="w-4 h-4 text-red-500" />
                      : <CheckCircleIcon className="w-4 h-4 text-emerald-500" />}
                  </div>
                  <div>
                    <p className="text-[13px] font-bold text-slate-900 mb-0.5">
                      Search: {s.company}
                      <span className={`ml-2 text-[10px] font-black uppercase ${statusColor(s.status)}`}>{s.status}</span>
                    </p>
                    <p className="text-[11px] text-slate-500">{s.user?.name ?? 'Unknown user'}</p>
                    <span className="text-[10px] font-mono text-slate-400">
                      {new Date(s.created_at).toLocaleTimeString()} UTC
                    </span>
                  </div>
                </div>
              ))}

              {/* Failed jobs */}
              {(data?.failed_jobs ?? []).map((j: any) => (
                <div key={j.id} className="flex items-start gap-4 bg-red-50/80 p-3 -mx-2 rounded-xl border border-red-100/50">
                  <div className="w-8 h-8 rounded-full bg-white border border-red-200 flex items-center justify-center shrink-0 shadow-sm">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="text-[13px] font-bold text-red-900 mb-0.5">Failed Job: {j.queue}</p>
                    <p className="text-[11px] text-red-700/80 leading-snug line-clamp-2">
                      {String(j.exception).split('\n')[0].substring(0, 80)}
                    </p>
                    <span className="text-[10px] font-mono text-red-400">
                      {new Date(j.failed_at).toLocaleTimeString()} UTC
                    </span>
                  </div>
                </div>
              ))}

            </div>
          </div>

          <button
            onClick={() => navigate('/searches')}
            className="w-full mt-5 bg-slate-100 hover:bg-slate-200 text-slate-600 py-3 rounded-xl text-[12px] font-extrabold uppercase tracking-widest transition-colors"
          >
            View All History
          </button>
        </div>

      </div>
    </div>
  );
}
